import { Injectable, Injector } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { lastValueFrom } from 'rxjs';
import { delayWhen, filter, map, switchMap, take } from 'rxjs/operators';
import { BlobService } from '../../../../cross-team-shared/asset-loading/blob.service';
import { AssetLoadError } from '../../../../cross-team-shared/misc-services/resources/url-request.service';
import {
  SensorFrame2D,
  SensorRendererState,
} from '../../../../cross-team-shared/state/sensor-renderer-state';
import { LoadImageService } from '../../../../shared/misc-services/load-image.service';
import { ViewBox } from '../../../../team-apis/content/view-box';
import {
  AbstractAssetInitializationService,
  AnnotationAsset,
} from '../../../shared/asset-loading/abstract-asset-initialization.service';
import {
  HACKY_VIEW_BOX_PADDING,
  ViewBoxService,
} from '../../../shared/utils/view-box.service';
import { VideoContentStore } from '../../state/video-content-store';
import { VideoPerFrameAssetLoadingService } from './video-per-frame-asset-loading.service';
import { VideoUnzipService } from './video-unzip.service';

interface Asset extends AnnotationAsset {
  sensorFrames: Array<SensorFrame2D | undefined>;
  firstImage: HTMLImageElement;
}

@Injectable()
export class VideoAssetInitializationService extends AbstractAssetInitializationService<Asset> {
  protected assetType = 'video';

  constructor(
    protected perFrameAssetLoadingService: VideoPerFrameAssetLoadingService,
    protected store: VideoContentStore,
    private blobService: BlobService,
    private loadImageService: LoadImageService,
    private unzipService: VideoUnzipService,
    private viewboxService: ViewBoxService,
    injector: Injector,
  ) {
    super(injector);
    this.subscribeTo(
      this.perFrameAssetLoadingService.reset$.pipe(
        switchMap(() =>
          this.perFrameAssetLoadingService.frameLoaded$.pipe(
            delayWhen(() =>
              this.store('status').$.pipe(
                filter((status) => status === 'ready'),
              ),
            ),
          ),
        ),
      ),
      this.updateStoreWithFrame,
    );
  }

  protected handleAssetLoadSuccess(asset: Asset, start: number) {
    const { firstImage } = asset;
    this.store.assign({
      status: 'ready',
      sensorRendererState: new SensorRendererState(
        asset.sensorFrames,
        firstImage.height,
        firstImage.width,
      ),
    });

    const viewBox: ViewBox = [0, 0, firstImage.width, firstImage.height];
    this.viewboxService.initializeViewBox(viewBox, HACKY_VIEW_BOX_PADDING);

    this.trackAssetLoadEventIfNotPfal(asset, start);
    return {
      width: firstImage.width,
      height: firstImage.height,
      frameCount: asset.sensorFrames.length,
      // If the first frame is loaded, then it's a zip asset so all other
      // frames are loaded too
      allFramesLoaded: isDefined(asset.sensorFrames[0]),
    };
  }

  protected override handleAssetLoadFailure(
    error: any,
    assetLoadStart: number,
  ) {
    super.handleAssetLoadFailure(error, assetLoadStart);
    this.perFrameAssetLoadingService.reset$.next();
  }

  protected releaseFrame(sensorFrame: SensorFrame2D) {
    this.blobService.releaseBlobUrl(sensorFrame.imageUrl);
  }

  protected override async startPerFrameLoading(url: string) {
    const manifest = await this.perFrameAssetLoadingService.loadManifest(url);
    if (manifest.frame_asset_ids.length === 0) {
      throw new AssetLoadError('No frames were provided');
    }
    this.perFrameAssetLoadingService.manifest$.next(manifest);

    const firstFrame = await this.buildFirstFramePromise();
    const blobUrl = firstFrame.sensorFrame.imageUrl;
    const firstImage = await this.loadImageService.loadImage(blobUrl);
    const emptyUrls = manifest.frame_asset_ids.map(() => undefined);
    return { sensorFrames: emptyUrls, firstImage } as Asset;
  }

  protected override async loadZipAsset(url: string) {
    const { sensorFrames, eventTrackingValue } =
      await this.unzipService.fetchAndUnzip(url);

    if (sensorFrames.length === 0) {
      throw new AssetLoadError('No frames were provided');
    }

    const firstImageUrl = sensorFrames[0].imageUrl;
    assert(firstImageUrl, 'Frames should not be undefined in the zip world');
    const firstImage = await this.loadImageService.loadImage(firstImageUrl);
    return { sensorFrames, firstImage, url, eventTrackingValue };
  }

  private buildFirstFramePromise() {
    return lastValueFrom(
      this.perFrameAssetLoadingService.frameLoaded$.pipe(
        take(1),
        map((frame) => {
          if ('error' in frame) {
            throw frame.error;
          } else {
            return frame;
          }
        }),
      ),
    );
  }
}
